import { appKey } from '@app/shared/brand'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import { useAuth } from './auth'
import { useTRPC } from './trpc'

/**
 * A setting has ONE value, however many places read it.
 *
 * The control that changes a setting and the surface it changes are rarely the
 * same component — a reader's size is set in a row above the book and applied
 * inside it — so per-component state gives them a copy each: the control moves,
 * writes storage, and the thing it was pointing at never hears. It looks like a
 * control that does nothing, which is worse than one that is missing.
 *
 * So the value lives here, beside the storage it is written to, and every hook
 * reading a key subscribes to the same entry.
 */
const values = new Map<string, unknown>()
const listeners = new Map<string, Set<() => void>>()
/**
 * Which ACCOUNT's value has been taken for each key.
 *
 * By account rather than a bare "taken", because signing out does not reload
 * the page: the next person to sign in at the same machine would otherwise find
 * the key already taken, read at the last person's size, and — since a change
 * merges onto what is held — write that person's settings into their own
 * account the first time they touched a control.
 */
const adopted = new Map<string, string>()

/** Whose settings are held. Changing identity drops what belonged to the last. */
let held: string | null = null

function forget(): void {
  values.clear()
  adopted.clear()
  for (const group of listeners.values()) {
    for (const notify of group) notify()
  }
}

const storageKey = (key: string) => appKey('setting', key)

/**
 * The stored value, read once per key and then held.
 *
 * The reference has to be stable between reads or `useSyncExternalStore` sees a
 * new snapshot every render and loops, which is why parsing happens above this
 * rather than inside it.
 */
function raw(key: string): unknown {
  if (values.has(key)) return values.get(key)
  let stored: unknown
  try {
    const text = localStorage.getItem(storageKey(key))
    stored = text === null ? undefined : JSON.parse(text)
  } catch {
    stored = undefined
  }
  values.set(key, stored)
  return stored
}

function publish(key: string, next: unknown): void {
  values.set(key, next)
  for (const notify of listeners.get(key) ?? []) notify()
}

function listen(key: string, notify: () => void): () => void {
  let group = listeners.get(key)
  if (!group) {
    group = new Set()
    listeners.set(key, group)
  }
  group.add(notify)
  return () => {
    group.delete(notify)
  }
}

/**
 * One of the caller's own settings, held on this device and — once they have an
 * account — against that account, so the choice survives changing machine.
 *
 * `parse` belongs to whoever defines the setting and must ALWAYS return a
 * value: what is stored was written by some earlier version of this app, so a
 * control that has been dropped or a scale that has been renumbered leaves
 * values behind that no longer resolve. Falling back is the ordinary path
 * rather than the error path.
 *
 * **The device is not a cache of the account.** A shared book is read through a
 * link that opens no session, so a reader with no account has the device and
 * nothing else — which is exactly the reader most likely to want larger text.
 * The device answers first and always holds the answer; the account layers over
 * it for the people who have one.
 *
 * The third value says the account has answered — or that there is no account
 * to ask — so a caller whose first read must already be the account's value
 * (a read that would otherwise run once with the device's and again with the
 * account's) can wait for it. Most callers do not need it.
 */
export function useSetting<T>(
  key: string,
  parse: (raw: unknown) => T,
): [T, (next: T | ((current: T) => T)) => void, boolean] {
  const trpc = useTRPC()
  const { me } = useAuth()

  /*
   * Read synchronously, so the first paint is already the size somebody chose.
   * A setting applied in an effect is a page that visibly re-sets itself, and
   * this one governs the line a reader is in the middle of.
   */
  const stored = useSyncExternalStore(
    useCallback((notify: () => void) => listen(key, notify), [key]),
    useCallback(() => raw(key), [key]),
  )
  const value = useMemo(() => parse(stored), [stored, parse])

  const mine = useQuery({
    ...trpc.settings.mine.queryOptions(),
    enabled: !!me,
    staleTime: Number.POSITIVE_INFINITY,
  })
  /* The mutation's `mutate` keeps one identity across renders, where the
     result object holding it does not; the setter below depends on it alone. */
  const { mutate: save } = useMutation(trpc.settings.set.mutationOptions())
  /* The account this hook has seen the account's answer for. */
  const [settledFor, setSettledFor] = useState<string | null>(null)

  /* Whoever is signed in now owns what is held; the last person's goes. */
  useEffect(() => {
    const who = me?.userId ?? null
    if (who === held) return
    held = who
    forget()
  }, [me?.userId])

  /*
   * The account's value wins when it arrives, and only once per key per person.
   *
   * It answers "what did I last choose, anywhere", which is a better answer
   * than this machine's. It runs in an EFFECT rather than during render: two
   * components read one setting — the page that draws the controls and the
   * reader that obeys them — so publishing mid-render updates the one that has
   * not rendered yet, which React warns about and which shows the controls
   * disagreeing with the page for a frame. Nothing needs it sooner: the device
   * answers first by design.
   */
  useEffect(() => {
    const who = me?.userId
    if (!mine.data || !who) return
    if (adopted.get(key) !== who) {
      adopted.set(key, who)
      if (key in mine.data) publish(key, mine.data[key])
    }
    setSettledFor(who)
  }, [mine.data, key, me?.userId])
  const settled = !me || mine.isError || settledFor === me.userId

  /*
   * An updater rather than a value, because a setting is usually one field of
   * several and the caller merges into the rest. Merging into what the last
   * render saw loses a change made since — two controls pressed before a
   * repaint write the same base twice, and the second silently discards the
   * first. Reading through `raw` at the moment of the write cannot.
   */
  const set = useCallback(
    (next: T | ((current: T) => T)) => {
      const value =
        typeof next === 'function'
          ? (next as (current: T) => T)(parse(raw(key)))
          : next
      /*
       * A change of their own settles the key, so the account's copy no longer
       * gets adopted over it.
       *
       * The query is in flight for a moment after a book opens, and what it
       * carries is the value from BEFORE this press. Adopted afterwards it puts
       * the old size back — visibly, a beat after somebody enlarged the text —
       * and the next thing they change merges onto that stale base and saves
       * it, so the first change is lost on the server too.
       */
      const who = me?.userId
      if (who) adopted.set(key, who)
      publish(key, value)
      try {
        localStorage.setItem(storageKey(key), JSON.stringify(value))
      } catch {
        /* A device that refuses to remember still honors the change it was
           just given; it simply forgets it on reload. */
      }
      if (me) save({ key, value })
    },
    [key, me, save, parse],
  )

  return [value, set, settled]
}
